import omit from 'lodash/omit';
import {
  REMOVE_ALL_ASSET_STATUS_FILTER,
  REMOVE_ALL_FILTERS,
  REMOVE_ALL_OBSERVATION_TYPE_FILTER,
  REMOVE_ASSET_STATUS_FILTER,
  REMOVE_OPENED_LAYER_ID_FILTER,
  REMOVE_SELECTED_LAYER_IDS_FILTER,
  REMOVE_SELECTED_PLAN_BAND_ID,
  REMOVE_SELECTED_PLAN_BAND_IDS,
  RESET_SELECTED_ASSETS,
  SELECT_ASSET_STATUS_FILTER,
  SELECT_OBSERVATION_TYPE_FILTER,
  SET_ASSET_LAYER_FILTER,
  SET_COLLAPSABLE,
  SET_DATE_FILTER,
  SET_FIELD_FILTERS,
  SET_HOVERED_ASSET_ID,
  SET_MULTI_SEARCH_MODE_FILTER,
  SET_OPENED_LAYER_ID_FILTER,
  SET_SEARCH_TAGS,
  SET_SEARCH_TEXT,
  SET_SELECTED_ASSET_TYPE_ID,
  SET_SELECTED_ASSETS,
  SET_SELECTED_PLAN_BAND_IDS,
  SET_SHOW_SELECTED_ONLY,
  SET_TABLE_FILTER,
  SET_TABLE_PAGINATION,
  SET_TABLE_SORTER,
  SET_TIME_TRAVEL_DATE,
  SET_SELECTED_PLAN_ID,
  INITIALIZE_ASSET_MANAGER,
  SET_SHOW_PLAN_VIEW,
} from '../actions/actionTypes';
import { AssetManagerAction, AssetManagerStateType } from '../context';
import { getDefaultFilters, getResettableFilterDefaults } from '../utils/assetFilters';

export default (
  state: AssetManagerStateType,
  action: AssetManagerAction,
): AssetManagerStateType => {
  const { assetManagerId } = action.payload;
  const currentAssetManager = state[assetManagerId];
  const { selectedAssetTypeId, assetManagerAssetTypes } = currentAssetManager || {};
  const currentAssetManagerAssetType = (
    assetManagerAssetTypes?.[selectedAssetTypeId]
    || {});

  const updateAssetTypeValue = <K extends 'filters' | 'assetSelection'>(
    key: K,
    updates: Partial<
    AssetManagerStateType[string]['assetManagerAssetTypes'][string][K]
    >,
  ): AssetManagerStateType => ({
      ...state,
      [assetManagerId]: {
        ...currentAssetManager,
        assetManagerAssetTypes: {
          ...assetManagerAssetTypes,
          [selectedAssetTypeId]: {
            ...currentAssetManagerAssetType,
            [key]: {
              ...currentAssetManagerAssetType[key],
              ...updates,
              // Stamp filter changes so the twin treats them as an external
              // update (see lastExternalUpdate / getMaxDate in DigitalTwin).
              ...(key === 'filters' ? { lastFilterUpdate: new Date() } : {}),
            },
          },
        },
      },
    });

  // Update a filter and mark the selection as manually updated, so the twin
  // treats the resulting change as web-driven (see lastExternalUpdate).
  // A change in filter affects what's selected on the twin, so this is needed to trigger an update.
  const updateFilterAndTouchSelection = (
    updates: Partial<
    AssetManagerStateType[string]['assetManagerAssetTypes'][string]['filters']
    >,
  ): AssetManagerStateType => ({
    ...state,
    [assetManagerId]: {
      ...currentAssetManager,
      assetManagerAssetTypes: {
        ...assetManagerAssetTypes,
        [selectedAssetTypeId]: {
          ...currentAssetManagerAssetType,
          filters: {
            ...currentAssetManagerAssetType.filters,
            ...updates,
            lastFilterUpdate: new Date(),
          },
          assetSelection: {
            ...currentAssetManagerAssetType.assetSelection,
            lastManualSelectionUpdate: new Date(),
          },
        },
      },
    },
  });

  switch (action.type) {
    case INITIALIZE_ASSET_MANAGER: {
      const { assetTypes, defaultAssetTypeId } = action.payload;
      const existingDefaultAssetType = assetManagerAssetTypes?.[defaultAssetTypeId];
      return {
        ...state,
        [assetManagerId]: {
          selectedAssetTypeId: defaultAssetTypeId,
          assetManagerAssetTypes: {
            ...assetManagerAssetTypes,
            ...assetTypes.reduce((acc, assetType) => {
              const { id: assetTypeId } = assetType;
              if (assetTypeId === undefined || assetTypeId === null) return acc;

              if (assetTypeId === defaultAssetTypeId && existingDefaultAssetType) {
                acc[assetTypeId] = existingDefaultAssetType;
                return acc;
              }

              acc[assetTypeId] = {
                filters: getDefaultFilters(),
                assetSelection: {
                  showSelectedOnly: false,
                  selectedAssets: [],
                  lastManualSelectionUpdate: new Date(),
                },
              };
              return acc;
            }, {}),
          },
        },
      };
    }

    case SET_SEARCH_TAGS:
      return updateAssetTypeValue('filters', {
        searchTags: action.payload.searchTags,
      });

    case SET_SEARCH_TEXT:
      return updateAssetTypeValue('filters', {
        searchText: action.payload.searchText,
      });

    case SET_DATE_FILTER:
      return updateAssetTypeValue('filters', {
        dateFilter: action.payload.dateFilter,
      });

    case SET_SELECTED_PLAN_BAND_IDS:
      return updateAssetTypeValue('filters', {
        selectedBandIds: {
          ...currentAssetManagerAssetType?.filters.selectedBandIds,
          [action.payload.planId]: action.payload.currentSelectedBandIds,
        },
      });

    case SET_TIME_TRAVEL_DATE:
      return updateAssetTypeValue('filters', {
        timeTravelDate: action.payload.timeTravelDate,
      });

    case SET_SELECTED_ASSET_TYPE_ID:
      return {
        ...state,
        [assetManagerId]: {
          ...currentAssetManager,
          selectedAssetTypeId: action.payload.selectedAssetTypeId,
        },
      };

    case SELECT_ASSET_STATUS_FILTER: {
      const { id, toggledValue } = action.payload;
      const { ids, toggle } = currentAssetManagerAssetType?.filters.assetStatusFilter || {};

      if (toggle !== toggledValue) {
        return updateAssetTypeValue('filters', {
          assetStatusFilter: {
            toggle: toggledValue,
            ids: [id],
          },
        });
      }

      const newIds = ids.includes(id)
        ? ids.filter(idFromState => idFromState !== id)
        : [...ids, id];

      if (newIds.length === 0) {
        return updateAssetTypeValue('filters', {
          assetStatusFilter: {
            toggle: null,
            ids: [],
          },
        });
      }

      return updateAssetTypeValue('filters', {
        assetStatusFilter: {
          toggle,
          ids: newIds,
        },
      });
    }

    case SELECT_OBSERVATION_TYPE_FILTER: {
      const { id, toggledValue } = action.payload;
      const { observationTypeFilter } = currentAssetManagerAssetType?.filters || {};
      const newObservationTypeFilter = { ...observationTypeFilter };
      if (
        (observationTypeFilter?.[id] && observationTypeFilter?.[id] === toggledValue)
        || toggledValue === null
      ) {
        delete newObservationTypeFilter[id];
      } else {
        newObservationTypeFilter[id] = toggledValue;
      }

      return updateAssetTypeValue('filters', {
        observationTypeFilter: newObservationTypeFilter,
      });
    }

    case SET_ASSET_LAYER_FILTER:
      return updateFilterAndTouchSelection({
        selectedLayerIds: action.payload.selectedLayerIds,
      });

    case SET_OPENED_LAYER_ID_FILTER:
      return updateFilterAndTouchSelection({
        openedLayerId: action.payload.openedLayerId,
      });

    case SET_FIELD_FILTERS: {
      return updateAssetTypeValue('filters', {
        fieldFilters: action.payload.value.length > 0
          ? {
            ...currentAssetManagerAssetType?.filters.fieldFilters,
            [action.payload.key]: action.payload.value,
          }
          : omit(currentAssetManagerAssetType?.filters.fieldFilters, action.payload.key),
      });
    }

    case SET_TABLE_FILTER:
      return updateAssetTypeValue('filters', {
        tableFilter: action.payload.tableFilter,
      });

    case SET_TABLE_SORTER:
      return updateAssetTypeValue('filters', {
        tableSorter: action.payload.tableSorter,
      });

    case SET_TABLE_PAGINATION:
      return updateAssetTypeValue('filters', {
        pagination: action.payload.pagination,
      });

    case SET_MULTI_SEARCH_MODE_FILTER:
      return updateAssetTypeValue('filters', {
        multiSearchModeFilter: action.payload.multiSearchModeFilter,
      });

    case SET_SELECTED_ASSETS:
      return updateAssetTypeValue('assetSelection', {
        selectedAssets:
        Array.isArray(action.payload.selectedAssets)
          ? [...new Set(action.payload.selectedAssets)]
          : action.payload.selectedAssets,
        lastManualSelectionUpdate:
          (action.payload.manual)
            ? new Date()
            : currentAssetManagerAssetType?.assetSelection?.lastManualSelectionUpdate,
        showSelectedOnly: action.payload.selectedAssets.length === 0
          ? false : currentAssetManagerAssetType?.assetSelection?.showSelectedOnly,
      });

    case SET_HOVERED_ASSET_ID:
      return updateAssetTypeValue('assetSelection', {
        hoveredAssetId: action.payload.hoveredAssetId,
        hoverOrigin: action.payload.hoverOrigin,
      });

    case SET_SHOW_SELECTED_ONLY:
      return updateAssetTypeValue('assetSelection', {
        showSelectedOnly: action.payload.showSelectedOnly,
      });

    case REMOVE_ALL_FILTERS:
      return updateAssetTypeValue('filters', {
        ...currentAssetManagerAssetType?.filters,
        ...getResettableFilterDefaults(),
      });

    case REMOVE_SELECTED_PLAN_BAND_IDS:
      return updateAssetTypeValue('filters', {
        selectedBandIds: {},
      });

    case REMOVE_ASSET_STATUS_FILTER: {
      const { id } = action.payload;
      const { ids, toggle } = currentAssetManagerAssetType?.filters.assetStatusFilter || {};
      const filteredIds = ids.filter(statusId => statusId !== id);
      return updateAssetTypeValue('filters', {
        assetStatusFilter: {
          toggle: filteredIds.length ? toggle : null,
          ids: filteredIds,
        },
      });
    }

    case REMOVE_SELECTED_PLAN_BAND_ID: {
      const { id, planId } = action.payload;
      const currentSelectedBandIds = currentAssetManagerAssetType.filters.selectedBandIds[planId];
      const filteredIds = currentSelectedBandIds.filter(bandId => bandId !== id);

      if (filteredIds.length) {
        return updateAssetTypeValue('filters', {
          selectedBandIds: {
            ...currentAssetManagerAssetType.filters.selectedBandIds,
            [action.payload.planId]: filteredIds,
          },
        });
      }

      return updateAssetTypeValue('filters', {
        selectedBandIds: omit(currentAssetManagerAssetType?.filters.selectedBandIds, planId),
      });
    }

    case REMOVE_ALL_ASSET_STATUS_FILTER:
      return updateAssetTypeValue('filters', {
        assetStatusFilter: {
          toggle: null,
          ids: [],
        },
      });

    case REMOVE_ALL_OBSERVATION_TYPE_FILTER:
      return updateAssetTypeValue('filters', {
        observationTypeFilter: {},
      });

    case REMOVE_SELECTED_LAYER_IDS_FILTER:
      return updateAssetTypeValue('filters', {
        selectedLayerIds: [],
      });

    case REMOVE_OPENED_LAYER_ID_FILTER:
      return updateAssetTypeValue('filters', { openedLayerId: null });

    case RESET_SELECTED_ASSETS:
      return updateAssetTypeValue('assetSelection', {
        selectedAssets: [],
        showSelectedOnly: false,
        lastManualSelectionUpdate: new Date(),
      });

    case SET_COLLAPSABLE:
      if (!selectedAssetTypeId) return state;

      return {
        ...state,
        [assetManagerId]: {
          ...state[assetManagerId],
          assetManagerAssetTypes: {
            ...assetManagerAssetTypes,
            [selectedAssetTypeId]: {
              ...currentAssetManagerAssetType,
              collapsable: {
                ...currentAssetManagerAssetType?.collapsable,
                [action.payload.id]: action.payload.state,
              },
            },
          },
        },
      };

    case SET_SELECTED_PLAN_ID:
      if (!action.payload.selectedPlanId) {
        const { selectedPlanId, ...removed } = currentAssetManagerAssetType;
        return {
          ...state,
          [assetManagerId]: {
            ...currentAssetManager,
            assetManagerAssetTypes: {
              ...assetManagerAssetTypes,
              [selectedAssetTypeId]: removed,
            },
          },
        };
      }
      return {
        ...state,
        [assetManagerId]: {
          ...state[assetManagerId],
          assetManagerAssetTypes: {
            ...assetManagerAssetTypes,
            [selectedAssetTypeId]: {
              ...currentAssetManagerAssetType,
              selectedPlanId: action.payload.selectedPlanId,
            },
          },
        },
      };
    case SET_SHOW_PLAN_VIEW:
      return {
        ...state,
        [assetManagerId]: {
          ...state[assetManagerId],
          assetManagerAssetTypes: {
            ...assetManagerAssetTypes,
            [selectedAssetTypeId]: {
              ...currentAssetManagerAssetType,
              showPlanView: action.payload.showPlanView,
            },
          },
        },
      };
    default:
      return state;
  }
};
